import { autoinject, computedFrom } from "aurelia-framework";
import { LookupValueQuery } from "../../../../../constants/modules/lookup.constants";
import { LookupValue, LookupValueRequest } from "../../../../../models/lookups/lookup.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { LookupClientService } from "../../../../../services/lookups/lookup-client.service";
import { SafetyRatingReviewService } from "../../../../../services/vetting/safety-rating-review.service";
import { SafetyRatingReviewStatusEnum } from "../../../../../models/vetting/vetting.models";
import { NotificationType, NotificationClientService } from "../../../../../services/basics/notification-client.service";
import { Reflection } from "../../../../../utilities/reflection";

@autoinject
export class SafetyScoreReviewStatusChangeDialog {

    isDropdownOpen: boolean;
    reviewId: number;
    reviewStatusId: number;
    wasAcceptable1: boolean;
    canAcceptable1: boolean;
    canAcceptable2: boolean;
    currentReviewStatus: LookupValue;
    reviewStatuses: LookupValue[];
    meta: any = {};
    statusSnippet: string;
    correspondenceToRequestors: string;
    comments: string;
    subtaskTypes: number[] = [];
    noVetSuperOutcome: number;
    anyRfi: boolean;
    failedVetSuperOutcome: number;
    onSubmit: (outcome: boolean) => void;

    constructor(private dialogClientService: DialogClientService, private lookupClientService: LookupClientService,
        private reviewService: SafetyRatingReviewService, private notificationsClientService:NotificationClientService) {

    }

    @computedFrom(Reflection.getPropertyPath<SafetyScoreReviewStatusChangeDialog>(z => z.currentReviewStatus),
        Reflection.getPropertyPath<SafetyScoreReviewStatusChangeDialog>(z => z.currentReviewStatus.id),
        Reflection.getPropertyPath<SafetyScoreReviewStatusChangeDialog>(z => z.noVetSuperOutcome),
        Reflection.getPropertyPath<SafetyScoreReviewStatusChangeDialog>(z => z.failedVetSuperOutcome))
    get showSummary() {
        return this.currentReviewStatus &&
            (this.currentReviewStatus.id == SafetyRatingReviewStatusEnum.Accepted1 
                || this.currentReviewStatus.id == SafetyRatingReviewStatusEnum.Accepted2
                || this.currentReviewStatus.id == SafetyRatingReviewStatusEnum.Canceled
                || this.currentReviewStatus.id == SafetyRatingReviewStatusEnum.AcceptedFromSystem)
            && (this.noVetSuperOutcome > 0 || this.failedVetSuperOutcome > 0);
    }

    @computedFrom(Reflection.getPropertyPath<SafetyScoreReviewStatusChangeDialog>(z => z.currentReviewStatus),
        Reflection.getPropertyPath<SafetyScoreReviewStatusChangeDialog>(z => z.currentReviewStatus.id),
        Reflection.getPropertyPath<SafetyScoreReviewStatusChangeDialog>(z => z.noVetSuperOutcome),
        Reflection.getPropertyPath<SafetyScoreReviewStatusChangeDialog>(z => z.failedVetSuperOutcome))
    get showUnacceptableSummary()
    {
        return this.currentReviewStatus.id == SafetyRatingReviewStatusEnum.Rejected && (this.noVetSuperOutcome > 0 ||
            (!this.failedVetSuperOutcome || this.failedVetSuperOutcome < 1));
    }

    async activate(model) {
        this.anyRfi = model.anyRfi;
        this.reviewId = model.safetyRatingReviewId;
        this.reviewStatusId = model.safetyRatingReviewStatusId;
        this.wasAcceptable1 = model.wasAcceptable1;
        this.canAcceptable1 = model.canAcceptable1;
        this.canAcceptable2 = model.canAcceptable2;
        this.noVetSuperOutcome = model.noVetSuperOutcome;
        this.failedVetSuperOutcome = model.failed;
        this.onSubmit = model.onSubmit;

        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.SafetyRatingReviewStatuses; 
        const response = await this.lookupClientService.getLookup(request);
        this.reviewStatuses = response.activeValues
            .filter(x => (this.canAcceptable1 && !this.wasAcceptable1) || x.id != SafetyRatingReviewStatusEnum.Accepted1)
            .filter(x => (this.canAcceptable2 && this.wasAcceptable1) || x.id != SafetyRatingReviewStatusEnum.Accepted2);
        this.currentReviewStatus = this.reviewStatuses.filter(x => x.id == this.reviewStatusId)[0];
        if (!this.currentReviewStatus) {
            // There's a status mismatch - Need to wait for status to be updated from signalR
            this.currentReviewStatus = new LookupValue(); // Default value to avoid null reference
            this.notificationsClientService.show("Error", `Status is being updated, please try again later`, NotificationType.Error);
            this.close();
        }
    }

    @computedFrom(Reflection.getPropertyPath<SafetyScoreReviewStatusChangeDialog>(z => z.currentReviewStatus),
        Reflection.getPropertyPath<SafetyScoreReviewStatusChangeDialog>(z => z.reviewStatusId),
        Reflection.getPropertyPath<SafetyScoreReviewStatusChangeDialog>(z => z.currentReviewStatus.id))
    get submitEnabled(): boolean {
        return this.currentReviewStatus && this.reviewStatusId != this.currentReviewStatus.id;
    }

    async submit() {
        try {
            if (this.showSummary) {
                this.notificationsClientService.show("Error", `All tasks must have passed outcome before changing the status to Acceptable`, NotificationType.Error);
                return;
            } 

            if(this.showUnacceptableSummary)
            {
                if(this.noVetSuperOutcome > 0)
                {
                    this.notificationsClientService.show("Error", `All tasks must have passed outcome before changing the status to Acceptable`, NotificationType.Error);
                    return;
                }

                if(!this.failedVetSuperOutcome || this.failedVetSuperOutcome < 1)
                {
                    this.notificationsClientService.show("Error", `At least 1 rule with failed outcome is required before changing the status to Unacceptable`, NotificationType.Error);
                    return;
                }
            }
            
            // PX-15456 - Validate safety rating when accepting in terminal states
            if (this.reviewStatusId == SafetyRatingReviewStatusEnum.Accepted2 || this.reviewStatusId == SafetyRatingReviewStatusEnum.Canceled || this.reviewStatusId == SafetyRatingReviewStatusEnum.AcceptedFromSystem) 
            {
                this.notificationsClientService.show("Error", `Cannot change status when review is in a terminal state`, NotificationType.Error);
                return;
            }
            

            const meta = JSON.stringify(this.meta);
            await this.reviewService.changeStatus(this.reviewId, this.currentReviewStatus.id, this.comments, meta, this.subtaskTypes, this.statusSnippet, this.correspondenceToRequestors);

            if(this.onSubmit)
                this.onSubmit(true);
            //Hide dialog
            this.dialogClientService.hide();
        } catch (e) {
            if (e._content.key=="review-in-final-state") {
                this.notificationsClientService.show("Error", e._content.details, NotificationType.Error);
            }
        }
    }

    @computedFrom(Reflection.getPropertyPath<SafetyScoreReviewStatusChangeDialog>(z => z.currentReviewStatus),
        Reflection.getPropertyPath<SafetyScoreReviewStatusChangeDialog>(z => z.wasAcceptable1),
        Reflection.getPropertyPath<SafetyScoreReviewStatusChangeDialog>(z => z.currentReviewStatus.id))
    get isCloseOut(){
        return this.currentReviewStatus && (this.wasAcceptable1 && this.currentReviewStatus.id == SafetyRatingReviewStatusEnum.Accepted2)
                || this.currentReviewStatus.id == SafetyRatingReviewStatusEnum.Rejected;
    }

    close() {
        this.dialogClientService.hide();
    }

    changeReviewStatus(value: LookupValue) {
        if (!this.currentReviewStatus || this.currentReviewStatus.id != value.id)
            this.meta = {};
        this.currentReviewStatus = value;
    }

    openDropdown(event) {
        event.stopPropagation();
        this.isDropdownOpen = !this.isDropdownOpen;
    }

    closeDropdown() {
        this.isDropdownOpen = false;
    }

    getStatus(status: string) {
        if (status) {
            status = status.split(' ').join('-').toLowerCase();
            
            if(status.indexOf('(') != -1){
                status = status.substr(0, status.indexOf('(') -1);
            }
        }

        return status;
    }
}